import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { fmtMXN, fmtDate, fmtDateTime, daysFromNow } from "../lib/api";
import { useAuth } from "../lib/auth";
import Topbar from "../components/Topbar";
import PizzaSpinner from "../components/PizzaSpinner";
import StatusBadge from "../components/StatusBadge";
import ExpenseCategoryBadge from "../components/ExpenseCategoryBadge";
import {
  ArrowLeft, CheckCircle2, XCircle, CreditCard, Calendar, Building2,
  FileText, Receipt, Clock, User, AlertTriangle, Banknote, RotateCw,
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";

function Timeline({ expense }) {
  const { t } = useTranslation();
  const steps = [
    {
      key: "created",
      label: t("expenses.registered"),
      icon: FileText,
      done: true,
      who: expense.created_by_name,
      when: expense.created_at,
    },
    {
      key: "approval",
      label: expense.status === "rejected" ? t("expenses.rejectedStep") : t("expenses.approvalStep"),
      icon: expense.status === "rejected" ? XCircle : CheckCircle2,
      done: ["approved", "paid", "rejected"].includes(expense.status),
      who: expense.approved_by_name,
      when: expense.approved_at,
      negative: expense.status === "rejected",
    },
    {
      key: "paid",
      label: t("expenses.paidStep"),
      icon: CreditCard,
      done: expense.status === "paid",
      who: null,
      when: expense.paid_at,
    },
  ];

  return (
    <div className="space-y-3">
      {steps.map((s, i) => {
        const Icon = s.icon;
        return (
          <div key={s.key} className="flex items-start gap-3" data-testid={`exp-timeline-${s.key}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
              s.negative ? "bg-red-100 text-red-700"
                : s.done ? "bg-emerald-100 text-emerald-700"
                : "bg-slate-100 text-slate-400"
            }`}>
              <Icon className="w-4 h-4" strokeWidth={2.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold text-[#1A1A1A]">{s.label}</div>
              {s.done && s.when && (
                <div className="text-[11px] text-slate-500">
                  {fmtDateTime(s.when)}{s.who && ` · ${s.who}`}
                </div>
              )}
              {!s.done && <div className="text-[11px] text-slate-400">{t("expenses.pendingStep")}</div>}
            </div>
            {i < steps.length - 1 && (
              <div className={`absolute w-px h-6 mt-9 ml-4 ${s.done ? "bg-emerald-300" : "bg-slate-200"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

export default function ExpenseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [exp, setExp] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const load = async () => {
    const { data } = await api.get(`/operating-expenses/${id}`);
    setExp(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!exp) return <><Topbar title={t("expenses.detailTitle")} /><PizzaSpinner /></>;

  const canApprove = user?.role === "corporate_admin" && exp.status === "pending_approval";
  const canPay = user?.role === "treasury" && exp.status === "approved";
  const due = daysFromNow(exp.due_date);
  const isOverdue = exp.status !== "paid" && due !== null && due < 0;

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/operating-expenses/${id}/decision`, { action: "approve", notes: "Aprobado" });
      toast.success(t("expenses.approvedToast"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("expenses.approveError"));
    } finally { setBusy(false); }
  };

  const reject = async () => {
    if (!rejectReason.trim()) return toast.error(t("expenses.reasonNeeded"));
    setBusy(true);
    try {
      await api.post(`/operating-expenses/${id}/decision`, { action: "reject", notes: rejectReason.trim() });
      toast.success(t("expenses.rejectedToast"));
      setRejectMode(false); setRejectReason("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("expenses.rejectError"));
    } finally { setBusy(false); }
  };

  const markPaid = async () => {
    setBusy(true);
    try {
      await api.post(`/operating-expenses/${id}/pay`, {
        payment_date: new Date().toISOString(),
        payment_method: "transfer",
        bank_reference: `REF-${Math.floor(Math.random() * 1000000)}`,
      });
      toast.success(t("expenses.paidToast", { amount: fmtMXN(exp.total) }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("expenses.payError"));
    } finally { setBusy(false); }
  };

  return (
    <>
      <Topbar title={`${t("expenses.detailTitle")} ${exp.folio}`} subtitle={exp.description} />
      <div className="p-6 max-w-5xl mx-auto space-y-5">
        <button
          data-testid="exp-back-btn"
          onClick={() => navigate("/gastos")}
          className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#1A1A1A]"
        >
          <ArrowLeft className="w-4 h-4" /> {t("expenses.backToList")}
        </button>

        {isOverdue && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-center gap-3"
          >
            <AlertTriangle className="w-5 h-5 text-red-600" />
            <div className="text-sm text-red-800 font-semibold">
              {t("expenses.overdueWarn", { count: Math.abs(due), n: Math.abs(due) })}
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Main */}
          <div className="lg:col-span-2 space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-6">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("expenses.providerEmitter")}</div>
                  <div className="font-display font-extrabold text-2xl text-[#1A1A1A] mt-1">{exp.vendor_name}</div>
                  {exp.vendor_rfc && <div className="text-xs font-mono text-slate-500 mt-0.5">{t("expenses.rfcLabel", { rfc: exp.vendor_rfc })}</div>}
                </div>
                <div className="flex flex-col items-end gap-2">
                  <StatusBadge status={exp.status} />
                  <ExpenseCategoryBadge category={exp.category} subcategory={exp.subcategory} />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-100">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{t("expenses.subtotal")}</div>
                  <div className="text-sm font-mono mt-1">{fmtMXN(exp.subtotal)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{t("expenses.tax")}</div>
                  <div className="text-sm font-mono mt-1">{fmtMXN(exp.tax)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{t("common.total")}</div>
                  <div className="text-lg font-bold font-mono mt-1">{fmtMXN(exp.total)}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {t("expenses.dueLabel")}
                  </div>
                  <div className="text-sm mt-1">{fmtDate(exp.due_date)}</div>
                </div>
                {exp.billing_period_from && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{t("expenses.periodCol")}</div>
                    <div className="text-xs mt-1">{fmtDate(exp.billing_period_from)} → {fmtDate(exp.billing_period_to)}</div>
                  </div>
                )}
                {exp.reference_number && (
                  <div>
                    <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">{t("expenses.providerFolioCol")}</div>
                    <div className="text-xs font-mono mt-1">{exp.reference_number}</div>
                  </div>
                )}
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1">
                    <Building2 className="w-3 h-3" /> {t("expenses.applyTo")}
                  </div>
                  <div className="text-sm mt-1">{exp.branch_name}</div>
                </div>
              </div>

              {exp.vendor_bank_name && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 flex items-center gap-1">
                    <Banknote className="w-3 h-3" /> {t("expenses.bankData")}
                  </div>
                  <div className="text-sm mt-1">
                    {exp.vendor_bank_name} {exp.vendor_bank_account && (
                      <span className="font-mono text-slate-500">· ···{String(exp.vendor_bank_account).slice(-4)}</span>
                    )}
                  </div>
                </div>
              )}

              {exp.template_id && (
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-500">
                  <RotateCw className="w-3.5 h-3.5" />
                  {t("expenses.fromTemplate")}
                </div>
              )}
            </div>

            {exp.notes && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-2">{t("common.notes")}</div>
                <div className="text-sm text-slate-700 whitespace-pre-line">{exp.notes}</div>
              </div>
            )}

            {exp.rejection_reason && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
                <div className="text-[10px] uppercase tracking-wider font-bold text-red-700 mb-2">{t("expenses.rejectionReason")}</div>
                <div className="text-sm text-red-800">{exp.rejection_reason}</div>
              </div>
            )}

            {/* Vouchers */}
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-3">{t("expenses.vouchers")}</div>
              <div className="flex flex-wrap gap-2">
                <a
                  data-testid="exp-pdf-link"
                  href={exp.invoice_pdf_url || "#"}
                  target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold hover:bg-slate-100"
                >
                  <FileText className="w-4 h-4 text-red-600" /> {t("expenses.invoicePdf")}
                </a>
                {exp.invoice_xml_url && (
                  <a
                    data-testid="exp-xml-link"
                    href={exp.invoice_xml_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs font-semibold hover:bg-slate-100"
                  >
                    <FileText className="w-4 h-4 text-blue-600" /> {t("expenses.xmlCfdi")}
                  </a>
                )}
                {exp.status === "paid" && (
                  <a
                    data-testid="exp-receipt-link"
                    href={exp.receipt_url || "#"} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-semibold hover:bg-emerald-100"
                  >
                    <Receipt className="w-4 h-4 text-emerald-600" /> {t("expenses.paymentReceipt")}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-5">
            <div className="bg-white rounded-2xl border border-slate-200 p-5">
              <div className="flex items-center gap-2 mb-4">
                <Clock className="w-4 h-4 text-slate-500" />
                <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("expenses.timeline")}</span>
              </div>
              <Timeline expense={exp} />
            </div>

            {canApprove && !rejectMode && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2">
                <div className="text-sm font-bold text-[#1A1A1A] mb-2">{t("expenses.approvalDecision")}</div>
                <button
                  data-testid="exp-approve-btn"
                  onClick={approve} disabled={busy}
                  className="w-full btn-primary-chizy py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <CheckCircle2 className="w-4 h-4" /> {t("expenses.approveExpense")}
                </button>
                <button
                  data-testid="exp-reject-btn"
                  onClick={() => setRejectMode(true)} disabled={busy}
                  className="w-full py-2.5 bg-white border border-red-200 text-red-700 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 hover:bg-red-50"
                >
                  <XCircle className="w-4 h-4" /> {t("expenses.rejectExpense")}
                </button>
              </div>
            )}

            {canApprove && rejectMode && (
              <div className="bg-white rounded-2xl border border-red-200 p-5 space-y-2">
                <div className="text-sm font-bold text-red-800 mb-1">{t("expenses.explainReason")}</div>
                <textarea
                  data-testid="exp-reject-reason-input"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={3} placeholder={t("expenses.rejectPlaceholder")}
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm"
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => { setRejectMode(false); setRejectReason(""); }}
                    className="flex-1 py-2 bg-white border border-slate-200 rounded-lg text-xs font-semibold"
                  >{t("common.cancel")}</button>
                  <button
                    data-testid="exp-confirm-reject-btn"
                    onClick={reject} disabled={busy}
                    className="flex-1 py-2 bg-red-600 text-white rounded-lg text-xs font-bold disabled:opacity-50"
                  >{t("expenses.confirmReject")}</button>
                </div>
              </div>
            )}

            {canPay && (
              <div className="bg-white rounded-2xl border border-slate-200 p-5">
                <div className="text-sm font-bold text-[#1A1A1A] mb-3">{t("expenses.executePayment")}</div>
                <button
                  data-testid="exp-pay-btn"
                  onClick={markPaid} disabled={busy}
                  className="w-full btn-primary-chizy py-2.5 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <CreditCard className="w-4 h-4" />
                  {busy ? t("payments.processing") : t("expenses.payAmount", { amount: fmtMXN(exp.total) })}
                </button>
              </div>
            )}

            {exp.status === "paid" && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
                <CheckCircle2 className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                <div className="text-sm font-bold text-emerald-900">{t("expenses.paidExpense")}</div>
                <div className="text-xs text-emerald-700 mt-1">
                  {exp.bank_reference && <>{t("expenses.ref")} <span className="font-mono">{exp.bank_reference}</span><br /></>}
                  {fmtDateTime(exp.paid_at)}
                </div>
              </div>
            )}

            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 text-xs text-slate-600 space-y-1">
              <div className="flex items-center gap-1.5 font-semibold text-slate-700">
                <User className="w-3 h-3" /> {t("expenses.registeredBy")}
              </div>
              <div>{exp.created_by_name} · {fmtDateTime(exp.created_at)}</div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
